import React, { forwardRef } from "react";
import { cn } from "../../lib/utils.js";
import { mergeRefs } from "../../primitives/index.js";
import { useFormField } from "../../lib/form-field-context.js";
import { checkFiles } from "./file-rules.js";
import { FileUploadList } from "./FileUploadList.js";
import { FileUploadSurface } from "./FileUploadSurface.js";

/* ---------------- FileUpload (C8 #6) ----------------
   Drag-drop + browse with validation messaging (strategy §15, §24 line 843).
   Presentational: it owns *selection* and validation, not the transport — the
   app drives real upload status/progress through the controlled `value` as
   XHR/fetch events arrive, so "uploading/success/error" render without
   coupling to a network layer.

   a11y: in every variant a real <input type=file> sits inside a <label>
   (FileUploadSurface): click, the native dialog and the accessible name all
   work. useFormField() lets a FormField wire aria-describedby/invalid/required,
   and the role="alert" line announces rejections in every variant.
   states-check-ignore: the input is sr-only; FileUploadSurface rings its label. */

export interface UploadFile {
  id: string;
  file: File;
  status?: "pending" | "uploading" | "success" | "error";
  /** 0–100, shown as a bar while status === "uploading". */
  progress?: number;
  /** Message shown when status === "error". */
  error?: string;
}

export interface FileRejection {
  file: File;
  reason: "type" | "size" | "count";
  message: string;
}

/** `dropzone` (default) is today's drop area above the file list; `strip` is
 *  a one-line dashed drop row (icon, hint, Browse) above the same list;
 *  `button` is a label in the Button look (secondary, sm) over the native
 *  input, with no drop target and no list. `value`, `onChange` and the rules
 *  are the same in every variant (button + multiple appends to an unshown list). */
export type FileUploadVariant = "dropzone" | "strip" | "button";

export interface FileUploadProps {
  /** Comma list of extensions/mime globs, e.g. ".pdf,image/*". */
  accept?: string;
  multiple?: boolean;
  /** Per-file size cap in bytes. */
  maxSize?: number;
  /** Cap on the total number of files. */
  maxFiles?: number;
  disabled?: boolean;
  /** Controlled file list (the app drives status/progress). */
  value?: UploadFile[];
  defaultValue?: UploadFile[];
  onChange?: (files: UploadFile[]) => void;
  onReject?: (rejections: FileRejection[]) => void;
  id?: string;
  className?: string;
  /** Instruction line inside the dropzone (e.g. "PNG or PDF, up to 5 MB").
   *  The strip shows it as its one line; the button variant does not show it. */
  hint?: React.ReactNode;
  /** Layout; default `dropzone` (see FileUploadVariant). */
  variant?: FileUploadVariant;
  /** The button variant's text (FilterMenu's name). Default "Upload". */
  triggerLabel?: string;
}

/* Only the button variant sizes to its content; the drop variants fill. */
const WRAPPER: Record<FileUploadVariant, string> = {
  dropzone: "w-full",
  strip: "w-full",
  button: "inline-flex flex-col items-start",
};

export const FileUpload = forwardRef<HTMLInputElement, FileUploadProps>(
  function FileUpload(
    {
      accept,
      multiple = false,
      maxSize,
      maxFiles,
      disabled,
      value,
      defaultValue,
      onChange,
      onReject,
      id,
      className,
      hint,
      variant = "dropzone",
      triggerLabel = "Upload",
    },
    forwardedRef,
  ) {
    const field = useFormField();
    const autoId = React.useId();
    const inputId = field?.id ?? id ?? autoId;
    const inputRef = React.useRef<HTMLInputElement>(null);
    const counter = React.useRef(0);

    const isControlled = value !== undefined;
    const [internal, setInternal] = React.useState<UploadFile[]>(
      defaultValue ?? [],
    );
    const files = isControlled ? value : internal;

    const [rejections, setRejections] = React.useState<FileRejection[]>([]);

    const commit = (list: UploadFile[]) => {
      if (!isControlled) setInternal(list);
      onChange?.(list);
    };

    const addFiles = (incoming: FileList | null) => {
      if (!incoming || disabled) return;
      const picked = Array.from(incoming);
      const { accepted, rejected } = checkFiles(picked, accept, maxSize);

      let nextList: UploadFile[];
      if (!multiple) {
        nextList = accepted.slice(0, 1).map((file) => ({
          id: `${inputId}-f${counter.current++}`,
          file,
          status: "pending" as const,
        }));
      } else {
        const room =
          maxFiles != null ? Math.max(0, maxFiles - files.length) : Infinity;
        const take = accepted.slice(0, room);
        for (const f of accepted.slice(room)) {
          rejected.push({
            file: f,
            reason: "count",
            message: `${f.name}: over the ${maxFiles}-file limit.`,
          });
        }
        nextList = [
          ...files,
          ...take.map((file) => ({
            id: `${inputId}-f${counter.current++}`,
            file,
            status: "pending" as const,
          })),
        ];
      }

      setRejections(rejected);
      if (rejected.length) onReject?.(rejected);
      if (nextList !== files) commit(nextList);
    };

    const remove = (fid: string) => {
      if (disabled) return;
      commit(files.filter((f) => f.id !== fid));
    };

    return (
      <div className={cn(WRAPPER[variant], className)}>
        <FileUploadSurface
          variant={variant}
          disabled={disabled}
          invalid={field?.invalid}
          hint={hint}
          triggerLabel={triggerLabel}
          onDropFiles={addFiles}
        >
          <input
            ref={mergeRefs(inputRef, forwardedRef)}
            id={inputId}
            type="file"
            accept={accept}
            multiple={multiple}
            disabled={disabled}
            aria-describedby={field?.describedBy}
            aria-invalid={field?.invalid || undefined}
            aria-required={field?.required || undefined}
            onChange={(e) => {
              addFiles(e.target.files);
              e.target.value = ""; // allow re-selecting the same file
            }}
            className="sr-only"
          />
        </FileUploadSurface>

        {variant !== "button" && (
          <FileUploadList files={files} disabled={disabled} onRemove={remove} />
        )}

        {rejections.length > 0 && (
          <p role="alert" className="text-dense-1 text-pb-red-text mt-1">
            {rejections.map((r) => r.message).join(" ")}
          </p>
        )}
      </div>
    );
  },
);
